<template>
  <main>
    <v-container>
      <v-row>
        <v-col>
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-card outlined>
              <!-- Tabs -->
              <v-tabs v-model="tab" background-color="primary" dark show-arrows>
                <v-tab>Details</v-tab>
                <v-tab>Location</v-tab>
              </v-tabs>

              <!-- Tab Items -->
              <v-tabs-items v-model="tab">
                <v-tab-item :transition="false" :reverse-transition="false" class="px-4 py-4">
                  <v-card outlined class="mb-4">
                    <v-tabs v-model="nameTranslationTab" show-arrows>
                      <v-tab v-for="item in Object.keys(formData.name)" :key="item">{{ item }}</v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="nameTranslationTab">
                      <v-tab-item
                        v-for="item in Object.keys(formData.name)"
                        :key="item"
                        :transition="false"
                        :reverse-transition="false"
                        class="px-4 py-4"
                      >
                        <v-text-field
                          v-model="formData.name[item]"
                          :label="`Name (${item})`"
                          :required="!!(item == 'en')"
                          :rules="item == 'en' ? rules.name : [v => true]"
                        />
                      </v-tab-item>
                    </v-tabs-items>
                  </v-card>
                  <v-card outlined class="mb-4">
                    <v-tabs v-model="descriptionTranslationTab" show-arrows>
                      <v-tab v-for="item in Object.keys(formData.description)" :key="item">{{ item }}</v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="descriptionTranslationTab">
                      <v-tab-item
                        v-for="item in Object.keys(formData.description)"
                        :key="item"
                        :transition="false"
                        :reverse-transition="false"
                        class="px-4 py-4"
                      >
                        <v-textarea
                          v-model="formData.description[item]"
                          :label="`Description (${item})`"
                          :required="!!(item == 'en')"
                          :rules="item == 'en' ? rules.description : [v => true]"
                        />
                      </v-tab-item>
                    </v-tabs-items>
                  </v-card>
                  <v-select v-model="formData.type" :items="types" :rules="rules.type" label="Event Type" required />
                  <datetime-picker
                    :datetime="parseISO(formData.date_from)"
                    label="Date From"
                    clearable
                    @input="onInputSetDate($event, 'date_from')"
                  />
                  <datetime-picker
                    :datetime="parseISO(formData.date_to)"
                    label="Date To"
                    clearable
                    @input="onInputSetDate($event, 'date_to')"
                  />
                  <v-text-field v-model="formData.website" type="url" label="Website" />
                  <v-select-image
                    v-model="formData.featured_image"
                    :size="250"
                    title="Featured Image"
                    label="Featured Image"
                    show-extra-previews
                  />
                  <v-checkbox v-model="formData.featured" label="Featured Event?" />
                </v-tab-item>
                <v-tab-item :transition="false" :reverse-transition="false" class="px-4 py-4">
                  <vuetify-algolia-places v-model="place" debounce />
                  <v-text-field v-model="formData.address_line_1" label="Address Line 1" />
                  <v-text-field v-model="formData.address_line_2" label="Address Line 2" />
                  <v-text-field v-model="formData.city" label="City" />
                  <v-text-field v-model="formData.state" label="State" />
                  <v-text-field v-model="formData.postal_code" label="Post Code" />
                  <v-text-field v-model="formData.country" label="Country" />
                  <v-text-field v-model="formData.latitude" label="Latitude" />
                  <v-text-field v-model="formData.longitude" label="Longitude" />
                </v-tab-item>
              </v-tabs-items>
            </v-card>
          </v-form>
        </v-col>
      </v-row>
    </v-container>

    <v-container>
      <v-row>
        <v-col class="d-flex items-center justify-end">
          <v-btn :loading="loading.cancelling" :disabled="disabled" class="mr-4" text @click="cancelUpdatingResource">
            Cancel
          </v-btn>
          <v-btn
            :loading="loading.updatingAlt"
            :disabled="disabled"
            class="mr-4"
            color="primary"
            @click="updateResourceWithoutRedirect"
          >
            Update & Continue Editing
          </v-btn>
          <v-btn :loading="loading.updating" :disabled="disabled" color="primary" @click="updateResourceWithRedirect">
            Update {{ resourceType }}
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </main>
</template>

<script>
import { format, parseISO, formatISO } from 'date-fns'
import { mapGetters, mapActions } from 'vuex'

import PermissionsHandler from '@/mixins/permissions-handler'
import UpdateResource from '@/mixins/update-resource'
import DatetimePicker from '@/components/datetime-picker'
import VSelectImage from '@/components/forms/select-image'

export default {
  name: 'EventsEdit',
  components: { DatetimePicker, VSelectImage },
  mixins: [PermissionsHandler, UpdateResource],
  data() {
    return {
      resourceType: 'Event',
      resourcePrefix: 'content.events',
      nameTranslationTab: null,
      descriptionTranslationTab: null,
      rules: {
        name: [v => !!v || 'Name is required', v => (!!v && v.length > 0) || 'Name must be greater than 0'],
        description: [
          v => !!v || 'Description is required',
          v => (!!v && v.length > 0) || 'Name must be greater than 3',
        ],
        type: [v => !!v || 'Type is required'],
      },
      formData: {
        name: { en: null, de: null, es: null, fr: null, it: null, nl: null, pt: null, sw: null },
        description: { en: null, de: null, es: null, fr: null, it: null, nl: null, pt: null, sw: null },
        type: null,
        date_from: null,
        date_to: null,
        website: null,
        featured: null,
        featured_image: null,
        address_line_1: null,
        address_line_2: null,
        city: null,
        state: null,
        postal_code: null,
        country: null,
        latitude: null,
        longitude: null,
      },
      types: ['conference', 'meetup'],
    }
  },
  computed: {
    ...mapGetters({
      resource: 'events/getEvent',
    }),
    place: {
      set(place) {
        this.formData.address_line_1 = place.hit.locale_names ? place.hit.locale_names[0] : null
        this.formData.address_line_2 = place.hit.locale_names ? place.hit.locale_names[1] : null
        this.formData.city = place.city ? place.city : null
        this.formData.state = place.hit.county ? place.hit.county[0] : null
        this.formData.postal_code = place.postcode ? place.postcode : null
        this.formData.country = place.country ? place.country : null
        this.formData.latitude = place.latlng.lat ? place.latlng.lat : null
        this.formData.longitude = place.latlng.lng ? place.latlng.lng : null
      },
      get() {
        return {
          address_line_1: this.formData.address_line_1,
          address_line_2: this.formData.address_line_2,
          city: this.formData.city,
          state: this.formData.state,
          postal_code: this.formData.postal_code,
          country: this.formData.country,
          latitude: this.formData.latitude,
          longitude: this.formData.longitude,
        }
      },
    },
  },
  methods: {
    onInputSetDate: function(date, dateType) {
      this.formData[dateType] = formatISO(date)
    },
    parseISO(date) {
      return date ? parseISO(date) : null
    },
    formatISOToCalender(date) {
      return date ? format(parseISO(date), 'yyyy-MM-dd hh:mm:ss', new Date()) : null
    },
    ...mapActions({
      findResource: 'events/find',
      update: 'events/update',
    }),
    async updateResource() {
      return await this.update({
        id: this.resource.id,
        formData: {
          ...this.formData,
          date_from: this.formatISOToCalender(this.formData.date_from),
          date_to: this.formatISOToCalender(this.formData.date_to),
        },
      })
    },
  },
}
</script>
